import { cn } from "@/lib/utils";
import { calc } from "a-calc";
import { Calculator } from "lucide-react";
import React, { useState } from "react";

type TNativeInputType =
	| "button"
	| "checkbox"
	| "color"
	| "date"
	| "datetime-local"
	| "email"
	| "file"
	| "hidden"
	| "image"
	| "month"
	| "number"
	| "password"
	| "radio"
	| "range"
	| "reset"
	| "search"
	| "submit"
	| "tel"
	| "text"
	| "time"
	| "url"
	| "week";

type TCommonInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & {
	enableMath?: boolean;
	classNameWrapper?: string;
	suffix?: string;
};

type TStandardInputProps = TCommonInputProps & {
	type?: TNativeInputType;
	currency?: never;
	currencyFractionDigits?: never;
};

type TCurrencyInputProps = TCommonInputProps & {
	type: "currency";
	currency: string;
	currencyFractionDigits?: number | "auto";
};

type InputProps = TStandardInputProps | TCurrencyInputProps;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, classNameWrapper, prefix, suffix, currency, currencyFractionDigits = "auto", ...props }, ref) => {
		const { mathProps } = useMathInputProps(props);
		const { currencyProps } = useCurrencyInputProps({ ...props, currency, currencyFractionDigits });

		return (
			<div
				className={cn(
					"relative basis-full ",
					// Prefix styles on wrapper
					prefix && [
						"before:content-[attr(data-prefix)]",
						"before:absolute before:left-3 before:top-1/2 before:-translate-y-1/2 before:z-10 [&:has(input:disabled)]:before:opacity-50",
						"before:text-muted-foreground before:pointer-events-none before:text-sm before:font-medium",
						"before:transition-all before:duration-75 before:ease-in-out",
					],
					// Suffix styles on wrapper
					suffix && [
						"after:content-[attr(data-suffix)] after:pointer-events-none",
						"after:absolute after:right-9 after:top-1/2 after:-translate-y-1/2 after:z-10 [&:has(input:disabled)]:after:opacity-50",
						"after:text-muted-foreground after:pointer-events-none after:text-sm after:font-medium",
						"after:transition-all after:duration-75 after:ease-in-out",
						// Move suffix more to the left on hover/focus for number input arrows
						// "hover:after:right-10 focus-within:after:right-10",
						// Handle Firefox (arrows always shown)
						"supports-[(-moz-appearance:none)]:after:right-6",
					],
					classNameWrapper,
				)}
				data-prefix={prefix}
				data-suffix={suffix}
			>
				<input
					ref={ref}
					className={cn(
						"border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
						"read-only:focus-visible:ring-0",
						prefix && "pl-8",
						className,
					)}
					{...props}
					{...mathProps}
					{...currencyProps}
				/>

				{props.enableMath && (
					<div
						title="Math enabled"
						className="border-input bg-info text-info-foreground absolute top-0 right-0 h-full border border-l pr-1 pl-1"
					>
						<Calculator size="14" className="m-auto mr-2 ml-2 h-full" />
					</div>
				)}
			</div>
		);
	},
);
Input.displayName = "Input";

export { Input };

const useMathInputProps = (props: TCommonInputProps) => {
	const [value, setValue] = useState<string>("");

	React.useEffect(() => {
		if (!props.enableMath || props.value == null || props.value === value) return;
		setValue(props.value.toString());
	}, [props.value]);

	if (!props.enableMath) return {};

	const sanitizeValue = (value: string) => {
		// Only allow math expressions
		return value.replace(/[^0-9+\-e.*/()]/g, "");
	};

	type InputEvent =
		| React.ChangeEvent<HTMLInputElement>
		| React.FocusEvent<HTMLInputElement>
		| React.KeyboardEvent<HTMLInputElement>;

	const handleValueCalculation = (e: InputEvent) => {
		const calculatedValue = calc(e.currentTarget.value, { _error: "Error with input" });
		if (calculatedValue === "Error with input") return;
		setValue(calculatedValue);

		props.onChange?.({
			...e,
			target: { ...e.target, value: calculatedValue },
		} as unknown as React.ChangeEvent<HTMLInputElement>);
	};

	const mathProps: React.InputHTMLAttributes<HTMLInputElement> = {
		value,
		type: "text",
		onChange: (e) => {
			const sanitizedValue = sanitizeValue(e.target.value);
			setValue(sanitizedValue);
		},
		onKeyDown: (e) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleValueCalculation(e);
				props.onKeyDown?.(e);
			}
		},
		onBlur: (e) => {
			handleValueCalculation(e);
			props.onBlur?.(e);
		},
	};

	return { mathProps };
};

type TCurrencyInputHookProps = TCommonInputProps & {
	type?: TNativeInputType | "currency";
	currency?: string;
	currencyFractionDigits?: number | "auto";
};

const useCurrencyInputProps = (props: TCurrencyInputHookProps) => {
	const { currency, currencyFractionDigits, ...inputProps } = props;
	const [displayValue, setDisplayValue] = useState<string>("");

	const fractionDigits = getCurrencyFractionDigits({
		currency,
		currencyFractionDigits,
		step: inputProps.step,
	});

	React.useEffect(() => {
		if (inputProps.type !== "currency" || !currency) return;
		setDisplayValue(formatCurrencyInputValue(inputProps.value, currency, fractionDigits));
	}, [currency, fractionDigits, inputProps.type, inputProps.value]);

	if (inputProps.type !== "currency" || !currency) return {};

	const allowNegative = allowsNegativeCurrencyValue(inputProps.min);

	const emitChange = (
		e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
		value: string,
	) => {
		inputProps.onChange?.({
			...e,
			target: { ...e.target, value },
		} as unknown as React.ChangeEvent<HTMLInputElement>);
	};

	const updateValue = (
		e: React.ChangeEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>,
		rawValue: string,
		caretDigitPosition: number,
	) => {
		const parsedValue = parseCurrencyInputValue(rawValue, { allowNegative, fractionDigits });
		const nextDisplayValue = formatCurrencyInputValue(parsedValue, currency, fractionDigits);

		setDisplayValue(nextDisplayValue);
		emitChange(e, parsedValue);
		restoreCurrencyCaret(e.currentTarget, nextDisplayValue, caretDigitPosition);
	};

	const currencyProps: React.InputHTMLAttributes<HTMLInputElement> = {
		...inputProps,
		value: displayValue,
		type: "text",
		inputMode: inputProps.inputMode ?? "decimal",
		onChange: (e) => {
			const caretDigitPosition = countCurrencyDigits(e.target.value.slice(0, e.target.selectionStart ?? 0));
			updateValue(e, e.target.value, caretDigitPosition);
		},
		onKeyDown: (e) => {
			const selectionStart = e.currentTarget.selectionStart;
			const selectionEnd = e.currentTarget.selectionEnd;
			const hasCollapsedSelection = selectionStart != null && selectionEnd != null && selectionStart === selectionEnd;

			if (e.key === "." && hasCollapsedSelection && e.currentTarget.value[selectionStart] === ".") {
				e.preventDefault();
				e.currentTarget.setSelectionRange(selectionStart + 1, selectionStart + 1);
			}

			if ((e.key === "Backspace" || e.key === "Delete") && hasCollapsedSelection) {
				const deletion = getCurrencyLiteralAwareDeletion({
					key: e.key,
					value: e.currentTarget.value,
					caretIndex: selectionStart,
				});

				if (deletion) {
					e.preventDefault();
					updateValue(e, deletion.value, deletion.caretDigitPosition);
				}
			}

			inputProps.onKeyDown?.(e);
		},
	};

	return { currencyProps };
};

function getCurrencyFractionDigits({
	currency,
	currencyFractionDigits,
	step,
}: {
	currency?: string;
	currencyFractionDigits?: number | "auto";
	step?: string | number;
}) {
	if (typeof currencyFractionDigits === "number") {
		return Math.max(0, Math.min(20, currencyFractionDigits));
	}

	const stepFractionDigits = getStepFractionDigits(step);
	if (stepFractionDigits != null) {
		return stepFractionDigits;
	}

	return (
		new Intl.NumberFormat("en-GB", {
			style: "currency",
			currency: currency ?? "GBP",
		}).resolvedOptions().maximumFractionDigits ?? 2
	);
}

function getStepFractionDigits(step?: string | number) {
	if (step == null || step === "any") return null;

	const stepValue = step.toString();
	const decimalPart = stepValue.includes(".") ? stepValue.split(".")[1] : undefined;
	return decimalPart?.length ?? 0;
}

function allowsNegativeCurrencyValue(min?: string | number) {
	if (min == null || min === "") return true;
	const parsedMin = Number.parseFloat(min.toString());
	return Number.isNaN(parsedMin) || parsedMin < 0;
}

function formatCurrencyInputValue(
	value: React.InputHTMLAttributes<HTMLInputElement>["value"],
	currency: string,
	fractionDigits: number,
) {
	if (value == null || value === "") return "";

	const numericValue = typeof value === "number" ? value : Number.parseFloat(parseCurrencyInputValue(value.toString()));
	if (Number.isNaN(numericValue)) return "";

	return new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency,
		currencyDisplay: "narrowSymbol",
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(numericValue);
}

function parseCurrencyInputValue(value: string, options: { allowNegative?: boolean; fractionDigits?: number } = {}) {
	const allowNegative = options.allowNegative ?? true;
	const fractionDigits = options.fractionDigits ?? 2;
	let parsedValue = "";
	let hasDecimalPoint = false;
	let hasMinus = false;

	for (const char of value) {
		if (/\d/.test(char)) {
			parsedValue += char;
			continue;
		}

		if (char === "." && fractionDigits > 0 && !hasDecimalPoint) {
			parsedValue += char;
			hasDecimalPoint = true;
			continue;
		}

		if (char === "-" && allowNegative && !hasMinus && parsedValue.length === 0) {
			parsedValue += char;
			hasMinus = true;
		}
	}

	if (hasDecimalPoint) {
		const [integerPart, decimalPart = ""] = parsedValue.split(".");
		return `${integerPart}.${decimalPart.slice(0, fractionDigits)}`;
	}

	return parsedValue;
}

function countCurrencyDigits(value: string) {
	return [...value].filter((char) => /\d/.test(char)).length;
}

function restoreCurrencyCaret(input: HTMLInputElement, formattedValue: string, digitPosition: number) {
	window.requestAnimationFrame(() => {
		const caretIndex = getCurrencyCaretIndex(formattedValue, digitPosition);
		input.setSelectionRange(caretIndex, caretIndex);
	});
}

function getCurrencyCaretIndex(value: string, digitPosition: number) {
	if (digitPosition <= 0) {
		const firstDigitIndex = value.search(/\d/);
		return firstDigitIndex === -1 ? value.length : firstDigitIndex;
	}

	let digitsSeen = 0;
	for (let index = 0; index < value.length; index += 1) {
		if (!/\d/.test(value[index] ?? "")) continue;
		digitsSeen += 1;
		if (digitsSeen === digitPosition) {
			return index + 1;
		}
	}

	const decimalIndex = value.indexOf(".");
	return decimalIndex === -1 ? value.length : decimalIndex;
}

function getCurrencyLiteralAwareDeletion({
	key,
	value,
	caretIndex,
}: {
	key: string;
	value: string;
	caretIndex: number;
}) {
	if (key === "Backspace") {
		const previousChar = value[caretIndex - 1];
		if (previousChar == null || /\d/.test(previousChar)) return null;

		const digitIndex = findPreviousCurrencyDigitIndex(value, caretIndex - 1);
		if (digitIndex == null) return null;

		return {
			value: `${value.slice(0, digitIndex)}${value.slice(digitIndex + 1)}`,
			caretDigitPosition: countCurrencyDigits(value.slice(0, digitIndex)),
		};
	}

	if (key === "Delete") {
		const currentChar = value[caretIndex];
		if (currentChar == null || /\d/.test(currentChar)) return null;

		const digitIndex = findNextCurrencyDigitIndex(value, caretIndex + 1);
		if (digitIndex == null) return null;

		return {
			value: `${value.slice(0, digitIndex)}${value.slice(digitIndex + 1)}`,
			caretDigitPosition: countCurrencyDigits(value.slice(0, digitIndex)),
		};
	}

	return null;
}

function findPreviousCurrencyDigitIndex(value: string, startIndex: number) {
	for (let index = startIndex; index >= 0; index -= 1) {
		if (/\d/.test(value[index] ?? "")) return index;
	}
	return null;
}

function findNextCurrencyDigitIndex(value: string, startIndex: number) {
	for (let index = startIndex; index < value.length; index += 1) {
		if (/\d/.test(value[index] ?? "")) return index;
	}
	return null;
}
